import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Download, RefreshCcw, Mail, Share2, Check, AlertTriangle, Clock } from "lucide-react";
import BrandMark from "../components/BrandMark";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" && !["expired", "exhausted"].includes(d) ? d : fallback;
};

export default function ResearchDownload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [started, setStarted] = useState(false);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const r = await axios.get(`${API}/research/download/${token}`);
      setInfo(r.data);
    } catch (e) {
      if (e?.response?.status === 404) setInfo({ state: "invalid" });
      else setLoadError("Не удалось связаться с сервером. Проверьте интернет-соединение.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await axios.post(`${API}/research/download/${token}`);
      setStarted(true);
      window.location.assign(r.data.url);
      setTimeout(load, 1500);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d === "expired" || d === "exhausted") await load();
      else setMsg({ type: "error", text: errText(e, "Не удалось начать скачивание. Попробуйте ещё раз через минуту.") });
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await axios.post(`${API}/research/download/${token}/resend`);
      setMsg({ type: "ok", text: `Новая ссылка отправлена на ${r.data.email_masked}. Эта ссылка больше не действует.` });
      setInfo((i) => ({ ...i, state: "replaced" }));
    } catch (e) {
      setMsg({ type: "error", text: errText(e, "Не удалось отправить письмо. Попробуйте позже.") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-black/10">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-4 px-4 sm:px-6 md:px-8 py-4 md:py-5">
          <BrandMark />
          <Link to="/research" className="text-sm text-foreground/60 hover:text-foreground" data-testid="nav-research">Исследование</Link>
        </div>
      </header>
      <main className="flex-1 max-w-screen-md w-full mx-auto px-6 sm:px-8 py-16 md:py-24" data-testid="research-download-page">
        <div className="label-mono">RESEARCH / 2026 · ЗАГРУЗКА</div>
        <h1 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-tight">
          Рынок стартапов в России: дифференцированный спрос
        </h1>
        <div className="mt-10">
          {loadError ? (
            <Card icon={AlertTriangle} testid="download-load-error" title="Страница не загрузилась" text={loadError}>
              <button className="btn-secondary" onClick={load} data-testid="download-retry-load"><RefreshCcw size={14} /> Повторить</button>
            </Card>
          ) : !info ? (
            <div className="label-mono py-10" data-testid="download-loading">Проверяем ссылку…</div>
          ) : (
            <StateView info={info} busy={busy} started={started} onDownload={download} onResend={resend} />
          )}
          {msg && (
            <div className={`mt-6 text-sm ${msg.type === "error" ? "text-[hsl(var(--destructive))]" : "text-foreground"}`} data-testid="download-message">
              {msg.text}
            </div>
          )}
        </div>
        <ShareBlock />
      </main>
      <Footer />
    </div>
  );
}

function StateView({ info, busy, started, onDownload, onResend }) {
  const resendBtn = (
    <button className="btn-secondary" onClick={onResend} disabled={busy} data-testid="download-resend-btn">
      <Mail size={14} /> Отправить новую ссылку на {info.email_masked}
    </button>
  );
  const ownCopy = (
    <Link to="/research#lead-form" className="btn-ghost" data-testid="download-request-own">Запросить свою копию</Link>
  );
  if (info.state === "ok") {
    const left = Math.max(0, info.max_downloads - info.downloads_used);
    return (
      <Card icon={Download} testid="download-ok" title={`${info.first_name}, ваш отчёт готов`}
        text={`${info.version_title} · PDF · ${info.pages_label}`}>
        <div className="w-full text-sm text-foreground/65 space-y-1" data-testid="download-limits">
          <div>Осталось скачиваний: <b className="tabular">{left}</b> из {info.max_downloads}</div>
          <div>Ссылка действует до {new Date(info.expires_at).toLocaleString("ru-RU", { dateStyle: "long", timeStyle: "short" })}</div>
        </div>
        <button className="btn-primary" onClick={onDownload} disabled={busy} data-testid="download-btn">
          <Download size={16} /> {started || info.in_grace ? "Скачать ещё раз" : "Скачать PDF"}
        </button>
        {(started || info.in_grace) && (
          <p className="w-full text-sm text-foreground/65" data-testid="download-grace-note">
            Скачивание началось. Если файл не сохранился, нажмите «Скачать ещё раз» в течение {info.grace_minutes} минут — лимит при этом не расходуется.
          </p>
        )}
      </Card>
    );
  }
  if (info.state === "expired" || info.state === "exhausted") {
    return (
      <Card icon={Clock} testid={`download-${info.state}`} title="Ссылка больше недействительна"
        text={info.state === "expired"
          ? "Срок действия персональной ссылки истёк."
          : "Лимит скачиваний по этой персональной ссылке исчерпан."}>
        <p className="w-full text-sm text-foreground/65">
          Если это ваша ссылка — отправим новую на ту же почту. Если ссылку переслал коллега — запросите свою копию, это бесплатно.
        </p>
        {resendBtn}
        {ownCopy}
      </Card>
    );
  }
  if (info.state === "replaced") {
    return <Card icon={Check} testid="download-replaced" title="Проверьте почту" text="Мы отправили новую персональную ссылку." />;
  }
  if (info.state === "not_ready") {
    return (
      <Card icon={Clock} testid="download-not-ready" title="Файл временно недоступен"
        text="Мы обновляем отчёт. Попробуйте открыть ссылку чуть позже или напишите на hello@hop.agency." />
    );
  }
  return (
    <Card icon={AlertTriangle} testid="download-invalid" title="Ссылка не найдена"
      text="Возможно, ссылка скопирована не полностью или была заменена новой.">
      {ownCopy}
    </Card>
  );
}

function Card({ icon: Icon, title, text, testid, children }) {
  return (
    <div className="border border-black/15 bg-[hsl(var(--card))] p-6 sm:p-10" data-testid={testid}>
      <Icon size={20} className="text-[hsl(var(--accent))]" />
      <h2 className="mt-4 text-2xl md:text-3xl font-medium leading-snug">{title}</h2>
      {text && <p className="mt-3 text-foreground/70 leading-relaxed">{text}</p>}
      {children && <div className="mt-8 flex flex-wrap items-center gap-4">{children}</div>}
    </div>
  );
}

function ShareBlock() {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/research`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="mt-16 border-t border-black/10 pt-10" data-testid="download-share-block">
      <div className="label-mono">КОЛЛЕГАМ</div>
      <p className="mt-3 text-lg font-medium">Коллегам тоже будет полезно?</p>
      <p className="mt-2 text-foreground/70">Ссылка выше персональная. Поделитесь страницей исследования — каждый получит свою копию.</p>
      <button className="btn-secondary mt-6" onClick={copy} data-testid="download-share-copy">
        {copied ? <Check size={14} /> : <Share2 size={14} />} {copied ? "Ссылка скопирована" : "Скопировать ссылку на исследование"}
      </button>
    </div>
  );
}
